import { IconCheck, IconShieldLock } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { SponsoredBrandsCampaignForm } from "@/components/campaign-creation/SponsoredBrandsCampaignForm";
import { SponsoredDisplayCampaignForm } from "@/components/campaign-creation/SponsoredDisplayCampaignForm";
import { CampaignCreationReview } from "@/components/campaign-creation/wizard/CampaignCreationReview";
import { ConfigurationStep } from "@/components/campaign-creation/wizard/ConfigurationStep";
import { CampaignCreationStepOneScreen } from "@/components/campaign-creation/wizard/StepOne";
import { useCampaignCreationWizard } from "@/components/campaign-creation/wizard/use-campaign-creation-wizard";
import { WizardFooter } from "@/components/campaign-creation/wizard/WizardFooter";
import { ErrorState } from "@/components/shared/error-state";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// The wizard's step and section components live in ./wizard; they stay
// importable from here because routes, stories and tests depend on this path.
export { CampaignCreationReview } from "@/components/campaign-creation/wizard/CampaignCreationReview";
export { ConfigurationStep } from "@/components/campaign-creation/wizard/ConfigurationStep";
export {
  CampaignCreationStepOneScreen,
  StepOne,
} from "@/components/campaign-creation/wizard/StepOne";
export { WizardFooter } from "@/components/campaign-creation/wizard/WizardFooter";
export type { SectionId } from "@/components/campaign-creation/wizard/wizard-navigation";

export function CampaignCreationWizard() {
  const { t } = useTranslation("campaignCreation");
  const {
    canWrite,
    backToCampaigns,
    initialPortfolioId,
    step,
    setStep,
    activeSection,
    selectedCampaignType,
    setSelectedCampaignType,
    selectedShopId,
    requestId,
    form,
    enterInput,
    setEnterInput,
    negativeKeywordInput,
    setNegativeKeywordInput,
    negativeProductInput,
    setNegativeProductInput,
    invalidProducts,
    setInvalidProducts,
    setProductCatalogBlocked,
    setSponsoredBrandsDirty,
    setSponsoredDisplayDirty,
    accounts,
    writableProfiles,
    capabilities,
    sponsoredBrandsEligibility,
    sponsoredDisplayCapabilities,
    sponsoredDisplayAvailability,
    isSubmitting,
    pendingShopId,
    cancelShopChange,
    confirmShopChange,
    selectShop,
    reconnectAmazon,
    continueStepOne,
    continueFromConfiguration,
    submitFromReview,
    submitSponsoredCampaign,
    editFromReview,
    discardCampaign,
  } = useCampaignCreationWizard();

  if (!canWrite) {
    return <CampaignCreationPermissionState onBack={backToCampaigns} />;
  }

  return (
    <CampaignCreationPageScrollFrame>
      {step > 1 ? <CampaignCreationPageHeader /> : null}
      {step === 3 ? <CampaignCreationStepper step={step} /> : null}

      <form className="flex flex-1 flex-col" onSubmit={(event) => event.preventDefault()}>
        {step === 1 ? (
          <CampaignCreationStepOneScreen
            isLoading={accounts.isLoading}
            profiles={writableProfiles}
            selectedShopId={selectedShopId}
            selectedCampaignType={selectedCampaignType}
            sponsoredBrandsEligibility={sponsoredBrandsEligibility}
            sponsoredDisplayAvailability={sponsoredDisplayAvailability}
            onSelectCampaignType={setSelectedCampaignType}
            onSelectShop={selectShop}
            onReconnect={reconnectAmazon}
            onRetrySponsoredDisplay={() => void sponsoredDisplayCapabilities.refetch()}
            onDiscard={discardCampaign}
            onNext={continueStepOne}
          />
        ) : step === 2 && selectedCampaignType === "sb" && selectedShopId && capabilities.data ? (
          <SponsoredBrandsCampaignForm
            shopId={selectedShopId}
            requestId={requestId}
            capabilities={capabilities.data}
            isSubmitting={isSubmitting}
            onDirtyChange={setSponsoredBrandsDirty}
            onCancel={() => setStep(1)}
            onSubmit={(payload) => submitSponsoredCampaign(payload, selectedShopId)}
          />
        ) : step === 2 &&
          selectedCampaignType === "sd" &&
          selectedShopId &&
          capabilities.data &&
          sponsoredDisplayCapabilities.data ? (
          <SponsoredDisplayCampaignForm
            shopId={selectedShopId}
            initialPortfolioId={initialPortfolioId}
            requestId={requestId}
            capabilities={capabilities.data}
            sponsoredDisplayCapabilities={sponsoredDisplayCapabilities.data}
            isSubmitting={isSubmitting}
            onDirtyChange={setSponsoredDisplayDirty}
            onCancel={() => setStep(1)}
            onReconnect={reconnectAmazon}
            onSubmit={(payload) => submitSponsoredCampaign(payload, selectedShopId)}
          />
        ) : step === 2 ? (
          capabilities.isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-40" />
              <Skeleton className="h-80" />
            </div>
          ) : capabilities.isError || !capabilities.data ? (
            <div className="flex flex-col items-center">
              <ErrorState
                message={t("capabilities.loadError")}
                onRetry={() => void capabilities.refetch()}
              />
              <Button type="button" variant="outline" onClick={() => void reconnectAmazon()}>
                {t("profile.empty.action")}
              </Button>
            </div>
          ) : (
            <ConfigurationStep
              capabilities={capabilities.data}
              form={form}
              selectedShopId={selectedShopId as string}
              initialPortfolioId={initialPortfolioId}
              activeSection={activeSection}
              enterInput={enterInput}
              onEnterInputChange={setEnterInput}
              negativeKeywordInput={negativeKeywordInput}
              onNegativeKeywordInputChange={setNegativeKeywordInput}
              negativeProductInput={negativeProductInput}
              onNegativeProductInputChange={setNegativeProductInput}
              invalidProducts={invalidProducts}
              onInvalidProductsChange={setInvalidProducts}
              onCatalogBlockingChange={setProductCatalogBlocked}
              onReconnect={reconnectAmazon}
            />
          )
        ) : capabilities.data && selectedShopId ? (
          <CampaignCreationReview
            shopName={
              writableProfiles.find((profile) => profile.shopId === selectedShopId)?.shopName ??
              selectedShopId
            }
            values={form.getValues()}
            capabilities={capabilities.data}
            onEdit={editFromReview}
          />
        ) : null}

        {step > 1 && selectedCampaignType === "sp" ? (
          <WizardFooter
            step={step}
            canContinueStepOne
            isSubmitting={isSubmitting}
            onBack={() => setStep((step - 1) as 1 | 2)}
            onContinue={() => {
              if (step === 2) continueFromConfiguration();
              else submitFromReview();
            }}
            onDiscard={discardCampaign}
          />
        ) : null}
      </form>

      <ShopChangeDialog
        open={Boolean(pendingShopId)}
        onDismiss={cancelShopChange}
        onConfirm={confirmShopChange}
      />
    </CampaignCreationPageScrollFrame>
  );
}

export function CampaignCreationPermissionState({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation("campaignCreation");
  return (
    <CampaignCreationPageScrollFrame>
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <IconShieldLock />
          </EmptyMedia>
          <EmptyTitle>{t("permission.title")}</EmptyTitle>
          <EmptyDescription>{t("permission.description")}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button onClick={onBack}>{t("actions.backToCampaigns")}</Button>
        </EmptyContent>
      </Empty>
    </CampaignCreationPageScrollFrame>
  );
}

export function CampaignCreationPageScrollFrame({ children }: { children: React.ReactNode }) {
  return (
    <main
      data-page-scroll-owner="campaign-creation"
      className="paper-scrollbar mx-auto flex min-h-0 w-full min-w-[960px] max-w-[1500px] flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-6 py-6"
    >
      {children}
    </main>
  );
}

export function CampaignCreationPageHeader() {
  const { t } = useTranslation("campaignCreation");
  return (
    <header className="flex min-h-10 items-center gap-2 border-b border-line pb-3 text-sm">
      <span className="text-muted-foreground">{t("breadcrumb.allSponsoredAds")}</span>
      <span className="text-muted-foreground">›</span>
      <span className="font-semibold text-foreground">{t("breadcrumb.createCampaign")}</span>
    </header>
  );
}

function CampaignCreationStepper({ step }: { step: 1 | 2 | 3 }) {
  const { t } = useTranslation("campaignCreation");
  return (
    <ol className="grid grid-cols-3 rounded-xl border border-line bg-surface p-2">
      {[1, 2, 3].map((value) => (
        <li
          key={value}
          aria-current={value === step ? "step" : undefined}
          className={cn(
            "flex items-center gap-3 rounded-lg px-4 py-3 text-sm text-muted-foreground",
            value === step && "bg-subtle text-ink",
          )}
        >
          <span
            className={cn(
              "grid size-7 place-items-center rounded-full border border-line font-mono text-xs",
              value < step && "border-primary bg-primary text-primary-foreground",
            )}
          >
            {value < step ? <IconCheck /> : value}
          </span>
          <span>
            <span className="block text-[10px] uppercase tracking-wider">
              {t("stepper.step", { number: value })}
            </span>
            <span className="font-medium">{t(`stepper.${value}`)}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function ShopChangeDialog({
  open,
  onDismiss,
  onConfirm,
}: {
  open: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !nextOpen && onDismiss()}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("shopChange.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("shopChange.description")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            {t("actions.keepEditing")}
          </AlertDialogClose>
          <AlertDialogClose render={<Button onClick={onConfirm} />}>
            {t("actions.discardAndSwitch")}
          </AlertDialogClose>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
